<template>
  <div class="category-details-overview">
    <div
      v-if="hasRightSideResponsesForSurvey || hasLeftSideResponsesForSurvey"
      class="row"
    >
      <div
        v-if="hasLeftSideResponsesForSurvey"
        class="category-details-chart-container col-12 col-md-6 px-0"
      >
        <BarChart
          v-if="!shouldShowResultsPerPerson"
          dataName="category-overview-bar"
          :data="barChartData"
          :numberOfResponses="category.numberOfResponses"
          :options="{
            height: 380,
            width: 600,
            shouldHideYAxis: true,
            denotingAnswers: true
          }"
          :theme="theme"
        />
        <div v-else class="h-100">
          <div class="d-flex justify-content-center my-5 w-100">
            <div
              v-for="resultName in Object.keys(donutDataPerResult)"
              :key="resultName"
              class="flex-grow-1 p-2 results-per-user"
            >
              <DonutChart
                :donutData="donutDataPerResult[resultName]"
                :tag="{
                  ...category,
                  name: `category-${category.name}-${resultName}`
                }"
                :theme="theme"
                :userIsAdminForCurrentGroup="
                  userIsAdminForCurrentGroupOrSuperAdmin
                "
              />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="category.averages && category.averages.length > 1"
        class="category-details-chart-container col-12 col-md-6 pb-10 pb-xl-15 px-0"
      >
        <LineChart
          dataName="category-overview-line"
          :data="lineChartData.data"
          :options="{ height: 380, width: 600 }"
          :series="lineChartSeries"
        />
      </div>
    </div>
    <div v-else class="d-flex justify-content-center">
      <div class="alert alert-info">
        <div class="d-flex align-items-center text-info">
          <i class="bi bi-info-circle-fill text-info fs-5 me-3"></i>
          {{ $t('widgetCategoriesBars.notEnoughResponses') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BarChart from '@/components/shared/charts/BarChart.vue'
import LineChart from '@/components/shared/charts/LineChart.vue'
import DonutChart from '@/components/shared/charts/DonutChart.vue'
import { getDonutDataForStatItem } from '@/components/shared/charts/chart-helpers'

import {
  getLineChartDataForStatItems,
  getBarChartDataForStatItem
} from '@/components/shared/charts/chart-helpers'

export default {
  name: 'CategoryDetailsOverview',
  components: {
    BarChart,
    LineChart,
    DonutChart
  },
  props: {
    category: {
      type: Object,
      default: () => {}
    },
    shouldShowResultsPerPerson: {
      type: Boolean,
      default: false
    },
    resultsPerPerson: {
      type: [Object, Boolean],
      default: false
    },
    userIsAdminForCurrentGroupOrSuperAdmin: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'default'
    }
  },
  data() {
    return {
      chartOptions: {
        domain: [0, 10],
        ticks: 10,
        height: 380,
        width: 600
      }
    }
  },
  computed: {
    barChartData() {
      return getBarChartDataForStatItem({ statItem: this.category })
    },
    donutDataPerResult() {
      if (this.resultsPerPerson) {
        return Object.keys(this.resultsPerPerson).reduce((res, result) => {
          const donutDataForResult = {
            ...getDonutDataForStatItem({
              statItem: { ...this.category, resultName: result },
              resultsPerPerson: this.resultsPerPerson,
              title: this.$t(
                'resultDetails.tagDetails.color.title',
                this.resultsPerPerson[result]
              ),
              description: this.$t(
                `resultDetails.tagDetails.color.description.${result}`,
                this.resultsPerPerson[result]
              ),
              theme: this.theme,
              localeNumber: this.$localeNumber,
              t: this.$t
            }),
            trend: {
              hasTrend: false
            }
          }
          if (donutDataForResult.currentValue) {
            res[result] = donutDataForResult
          }
          return res
        }, {})
      }
      return {}
    },
    hasLeftSideResponsesForSurvey() {
      if (this.shouldShowResultsPerPerson) {
        return Object.values(this.resultsPerPerson).some((v) => v > 0)
      } else {
        return this.barChartData.some((d) => d.value > 0)
      }
    },
    hasRightSideResponsesForSurvey() {
      return this.lineChartData?.data.length
    },
    lineChartData() {
      return getLineChartDataForStatItems({
        statItems: {
          [this.category.name]: this.category
        },
        statItemsTrend: {
          [this.category.name]: {
            averages: this.category.averages,
            regressionValues: this.category.regressionValues
          }
        },
        hasLinearRegression: this.hasLinearRegression,
        tr: this.$tr
      })
    },
    lineChartSeries() {
      if (this.hasLinearRegression) {
        return [
          { name: 'linearRegressionValue', title: 'linearRegressionValue' },
          {
            name: this.category.name,
            title: this.$tr(this.category.messages, 'title')
          }
        ]
      }
      return [
        {
          name: this.category.name,
          title: this.$tr(this.category.messages, 'title')
        }
      ]
    },
    hasLinearRegression() {
      return this.category.regressionValues?.length > 2
    }
  }
}
</script>

<style lang="scss">
.category-details-chart-container {
  display: flex;
  align-items: end;
  > div {
    width: 100%;
  }
}
</style>
